import { Component, OnInit } from '@angular/core';
import { DialogComponent, DialogService } from '../../services/modal-dialogs';
import { Invoice } from '../../models/domain/invoice';
import { AddInvoiceRequestItem, AddInvoiceRequestItemArray, AddInvoiceRequest } from '../../models/ss-api/requests/add.invoice.request';
import { ssApiService } from '../../services/ss-api.service';
import { BaseResponse } from '../../models/comm-http/responses/BaseResponse';
import { CreditCardArray } from '../../models/domain/credit.card';
import { SelectItem } from 'primeng/primeng';
import { SweetAlertService } from 'ng2-cli-sweetalert2';
import { EditInvoiceRequest } from '../../models/ss-api/requests/edit.invoice.request';
import { InvoiceItemArray } from '../../models/domain/invoice.item';
import { MemberDashboard } from '../../../app/models/ss-api/responses/member.dashboard.response';
import * as moment from 'moment'
import { Router } from '@angular/router';

export interface IAddInvoiceDialogModel {
    IsEditDialog: boolean;
    Invoice: Invoice;
    title: string;
    subTitle: string;
    message: string;
    MemberID: number;
    DashboardData: MemberDashboard;
}

@Component({
    selector: 'add-invoice-dialog',
    styleUrls: ['./add.invoice.dialog.css'],
    templateUrl: './add.invoice.dialog.html'
})

export class AddInvoiceDialog extends DialogComponent<IAddInvoiceDialogModel, boolean> implements IAddInvoiceDialogModel, OnInit {

    IsBusy: boolean = false;

    IsEditDialog: boolean = false;
    Invoice: Invoice;

    title: string;
    subTitle: string;
    message: string;

    MemberID: number;
    DashboardData: MemberDashboard;

    CreditCardID: number;
    LicenseID: number;
    GraceDay: number;
    ForceGrace: boolean;
    IgnoreDiscountItems: boolean = true;
    TryCapturePayment: boolean = true;
    DateTimeCreated: Date = new Date();
    DueDate: Date = moment(moment().format('YYYY-MM-DD')).toDate();

    IsBeforeBillingDate: boolean = false;

    Items: AddInvoiceRequestItemArray = [
        {
            ID: 0,
            Description: 'Description',
            Amount: 0
        }
    ];

    CCData: CreditCardArray;

    creditCards: SelectItem[] = [];
    selectedCreditCard: SelectItem;

    Licenses: SelectItem[] = [{
        label: 'No License',
        value: null
    }];
    selectedLicense: SelectItem;

    GraceDays: SelectItem[] = [
        {
            label: 'None',
            value: null
        },
        {
            label: '1',
            value: 1
        },
        {
            label: '5',
            value: 5
        },
        {
            label: '10',
            value: 10
        }
    ];
    selectedGraceDay: SelectItem;

    Total: number = 0;

    // CONSTRUCTOR
    constructor(private api: ssApiService, dialogService: DialogService, private sweetAlertService: SweetAlertService, public router: Router) {
        super(dialogService, router);
    }

    // ONINIT
    ngOnInit() {
        this.setUpCreditCards();

        if (this.IsEditDialog && this.Invoice != undefined) {
            this.Items = InvoiceItemArray.convertToInvoiceRequestItems(this.Invoice.Items);
            this.DateTimeCreated = this.Invoice.DateTimeCreated;
            this.DueDate = this.Invoice.DueDate;
        }

        this.selectedGraceDay = this.GraceDays[this.IndexForGraceDay()];

        this.updateTotal();
        this.DueDateChanged();
    }

    // CREDIT CARD METHODS
    async setUpCreditCards() {

        this.IsBusy = true;

        await this.api.getUsableCCards(this.MemberID).subscribe(
            (result: BaseResponse<CreditCardArray>) => {
                this.CCData = result.Data;

                // Sort the cards so default is always the first option
                let sortedCreditCards = this.CCData.sort(function (a, b) {
                    return (a.IsDefault === b.IsDefault) ? 0 : a.IsDefault ? -1 : 1;
                })

                sortedCreditCards.forEach(e => {

                    if (!e.IsActive || e.IsDeleted || (!e.IsDefault && e.Status == 'Expired'))
                        return;

                    let newCard: SelectItem = {
                        label: e.FirstName + ' ' + e.LastName + ' - XXXX XXXX XXXX ' + e.LastFour, value: e.ID
                    };
                    this.creditCards.push(newCard);
                });

                this.selectedCreditCard = this.creditCards[0];
                if (this.creditCards[0]) {
                    this.CreditCardID = this.creditCards[0].value;
                } else {
                    this.sweetAlertService.swal(
                        {
                            title: 'Error',
                            text: 'No Credit Cards On this Account.',
                            type: 'error',
                            showCancelButton: false,
                            showConfirmButton: true,
                            confirmButtonText: 'OK',
                        })
                        .then((response: any) => {
                            this.IsBusy = false;
                            this.close();
                        }, (dismiss) => {
                            this.IsBusy = false;
                            this.close();
                            return;
                        }); // keep this line to prevent promise error;
                }

                this.setupLicenses();

                this.IsBusy = false;
            },

            (error: any) => {
                this.IsBusy = false;
            }

        );



    }

    setupLicenses() {
        let licenses = [...this.DashboardData.OwnLicenses, ...this.DashboardData.DesignersLicenses];

        licenses.forEach((license) => {
            let item: SelectItem = {
                label: license.Key + ' - ' + license.MemberFirstName + ' ' + license.MemberLastName,
                value: license.ID
            }

            this.Licenses.push(item);
        })

        let license = this.Licenses[this.IndexForLicenseID()];

        this.selectedLicense = license ? license.value : null;

    }

    IndexForLicenseID() {
        if (this.Invoice) {
            let InvoiceLicense = this.Licenses.filter((license) => {
                return license.value == this.Invoice.LicenseID;
            })

            return this.Licenses.indexOf(InvoiceLicense[0]);
        } else {
            return 0;
        }
    }

    IndexForGraceDay() {
        if (this.Invoice) {
            let day = this.GraceDays.filter((grace) => {
                return grace.value == this.Invoice.GraceDay;
            })

            return this.GraceDays.indexOf(day[0]);
        } else {
            return 0;
        }
    }

    CreditCardChanged(item: SelectItem) {
        this.CreditCardID = item.value;
    }

    LicenseChanged(value) {
        this.LicenseID = value;
    }

    GraceDayChanged(item: SelectItem) {
        this.GraceDay = item.value;
    }

    getAddInvoiceRequest(): AddInvoiceRequest {

        let request = new AddInvoiceRequest();
        request.DateTimeCreated = this.DateTimeCreated;
        request.DueDate = this.DueDate;
        request.MemberID = this.MemberID;
        request.Items = this.Items;
        request.TryCapturePayment = this.TryCapturePayment;
        request.CreditCardID = this.CreditCardID;
        request.LicenseID = this.LicenseID;
        if (request.LicenseID > 0){
            //Good
        }
        else{
            request.LicenseID = undefined;
        }
        request.ForceGrace = this.ForceGrace ? this.ForceGrace : false;
        request.IgnoreDiscountItems = this.IgnoreDiscountItems;


        return request;
    }

    getEditInvoiceRequest(): EditInvoiceRequest {

        let request = new EditInvoiceRequest();
        request.DateTimeCreated = this.DateTimeCreated;
        request.DueDate = this.DueDate;
        request.MemberID = this.MemberID;
        request.Items = this.Items;
        request.TryCapturePayment = this.TryCapturePayment;
        request.CreditCardID = this.CreditCardID;
        request.LicenseID = this.LicenseID;
        if (request.LicenseID > 0){
            //Good
        }
        else{
            request.LicenseID = undefined;
        }
        request.ForceGrace = this.ForceGrace ? this.ForceGrace : false;        
        request.IgnoreDiscountItems = this.IgnoreDiscountItems;

        request.InvoiceID = this.Invoice.InvoiceID;

        return request;
    }

    DueDateChanged() {
        this.IsBeforeBillingDate = moment(moment().format('YYYY-MM-DD')).isBefore(this.DueDate);
    }

    async addInvoice() {

        this.IsBusy = true;

        let request = this.getAddInvoiceRequest();

        await this.sweetAlertService.swal(
            {
                title: 'Add Invoice?',
                text: 'Are you sure you want to add this invoice?',
                type: 'warning',
                showCancelButton: true,
                showConfirmButton: true,
                confirmButtonText: 'Yes, add!',
                cancelButtonText: 'Cancel',
                allowOutsideClick: false,
                showLoaderOnConfirm: true,
                preConfirm: () => {
                    return this.api.AddInvoice(request).toPromise();
                }
            })
            .then(response => {
                this.result = true;
                this.close();
                if (response.Result) {
                    this.sweetAlertService.swal(
                        {
                            title: 'Success!',
                            text: 'Invoice Added',
                            type: 'success',
                            showCancelButton: false,
                            showConfirmButton: false,
                            timer: 1500
                        })
                        .then((response: any) => {
                            this.IsBusy = false;

                        }, (dismiss) => {
                            this.IsBusy = false;
                            return;
                        }); // keep this line to prevent promise error;
                } else {
                    this.sweetAlertService.swal(
                        {
                            title: 'Error!',
                            text: 'Invoice was added but payment could not be processed.',
                            type: 'error',
                            showCancelButton: false,
                            showConfirmButton: true
                        })
                        .then((response: any) => {
                            this.IsBusy = false;

                        }, (dismiss) => {
                            this.IsBusy = false;
                            return;
                        }); // keep this line to prevent promise error;
                }
            }, (dismiss) => {
                // this.IsBusy = false;
                return;
            })
            .catch(error => {
                this.sweetAlertService.swal(
                    {
                        title: 'Error',
                        text: error.message,
                        type: 'error',
                        showCancelButton: false,
                        showConfirmButton: true,
                        confirmButtonText: 'OK',
                    })
                    .then((response: any) => {
                        this.result = false;
                        this.IsBusy = false;
                        this.close();
                    }, (dismiss) => {
                        this.IsBusy = false;
                        return;
                    }); // keep this line to prevent promise error;
            });

    }

    async editInvoice() {

        this.IsBusy = true;

        let request = this.getEditInvoiceRequest();

        await this.sweetAlertService.swal(
            {
                title: 'Save Changes to Invoice?',
                text: 'Are you sure you want to save these changes?',
                type: 'warning',
                showCancelButton: true,
                showConfirmButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'Cancel',
                allowOutsideClick: false,
                showLoaderOnConfirm: true,
                preConfirm: () => {
                    return this.api.EditInvoice(request).toPromise();
                }
            })
            .then(response => {
                this.result = true;
                this.close();
                this.sweetAlertService.swal(
                    {
                        title: 'Invoice Saved',
                        text: 'Shout \'HOORAY!\'',
                        type: 'success',
                        showCancelButton: false,
                        showConfirmButton: false,
                        timer: 1500
                    })
                    .then((response: any) => {
                        this.IsBusy = false;

                    }, (dismiss) => {
                        this.IsBusy = false;
                        return;
                    }); // keep this line to prevent promise error;
            })
            .catch(error => {
                this.sweetAlertService.swal(
                    {
                        title: 'Error',
                        text: error.message,
                        type: 'error',
                        showCancelButton: false,
                        showConfirmButton: true,
                        confirmButtonText: 'OK',
                    })
                    .then((response: any) => {
                        this.result = false;
                        this.IsBusy = false;
                        this.close();
                    }, (dismiss) => {
                        this.IsBusy = false;
                        return;
                    }); // keep this line to prevent promise error;
            });




    }


    addInvoiceItem() {
        let newInvoiceItem: AddInvoiceRequestItem = new AddInvoiceRequestItem();
        newInvoiceItem.Description = 'Description';
        newInvoiceItem.Amount = 0;
        this.Items.push(newInvoiceItem);
        this.updateTotal();
    }

    removeInvoiceItem(itemIndex: number) {
        this.Items.splice(itemIndex, 1);
        this.updateTotal();
    }

    updateTotal() {
        this.Total = 0;
        this.Items.forEach(e => {
            this.Total += e.Amount;
        });
    }

    GetCredits() {
        let credit = 0;

        if (this.DashboardData.Credit >= this.Total) {
            credit = this.Total;
        } else {
            credit = this.DashboardData.Credit;
        }

        return credit;
    }

    GetTotal() {
        let total = this.Total - this.DashboardData.Credit;

        if (total < 0) {
            total = 0;
        }

        return total;
    }

    confirm() {
        // we set dialog result as true on click on confirm button,
        // then we can get dialog result from caller code
        this.result = true;
        this.close();
    }


}
